import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { ArrowLeft, Mail, Server, CheckCircle2, AlertCircle } from 'lucide-react';
import HomeButton from '../components/HomeButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ConfiguracionSMTP = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    from_email: 'noreply@surnexa.cl',
    use_tls: true
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/smtp-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setFormData({
          ...response.data,
          smtp_password: '' // Don't populate password for security
        });
      }
    } catch (err) {
      console.log('No existing SMTP config found');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/smtp-config`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update formData with the saved config id
      if (response.data.config && response.data.config.id) {
        setFormData(prev => ({ ...prev, id: response.data.config.id }));
      }
      
      setSuccess('Configuración SMTP guardada exitosamente. Ahora puedes probar el envío.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    // First check if there's unsaved data
    if (!formData.smtp_host || !formData.smtp_user || !formData.smtp_password) {
      setError('Por favor, guarda la configuración antes de probar');
      return;
    }

    setTesting(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/smtp-config/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess(response.data.message || 'Email de prueba enviado correctamente');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Error al enviar email de prueba';
      setError(errorMsg);
      console.error('SMTP test error:', err);
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-2 mb-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver
          </Button>
          <HomeButton />
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-2xl">
              <Server className="h-6 w-6" />
              Configuración SMTP
            </CardTitle>
            <CardDescription>
              Configure el servidor de correo para el envío de códigos de autenticación de dos factores (2FA)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600 mx-auto"></div>
              </div>
            ) : (
              <>
                {error && (
                  <Alert variant="destructive" className="mb-4">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                {success && (
                  <Alert className="mb-4 bg-green-50 text-green-900 border-green-200">
                    <CheckCircle2 className="h-4 w-4" />
                    <AlertDescription>{success}</AlertDescription>
                  </Alert>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="smtp_host">Servidor SMTP *</Label>
                    <Input
                      id="smtp_host"
                      placeholder="smtp.example.com"
                      value={formData.smtp_host}
                      onChange={(e) => setFormData({ ...formData, smtp_host: e.target.value })}
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">Ejemplo: smtp.gmail.com, mail.surnexa.cl</p>
                  </div>

                  <div>
                    <Label htmlFor="smtp_port">Puerto SMTP *</Label>
                    <Input
                      id="smtp_port"
                      type="number"
                      value={formData.smtp_port}
                      onChange={(e) => setFormData({ ...formData, smtp_port: parseInt(e.target.value) })}
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">Común: 587 (TLS), 465 (SSL), 25 (sin encriptación)</p>
                  </div>

                  <div>
                    <Label htmlFor="smtp_user">Usuario SMTP *</Label>
                    <Input
                      id="smtp_user"
                      type="email"
                      placeholder="correo@example.com"
                      value={formData.smtp_user}
                      onChange={(e) => setFormData({ ...formData, smtp_user: e.target.value })}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="smtp_password">Contraseña SMTP *</Label>
                    <Input
                      id="smtp_password"
                      type="password"
                      placeholder="••••••••"
                      value={formData.smtp_password}
                      onChange={(e) => setFormData({ ...formData, smtp_password: e.target.value })}
                      required={!formData.id}
                    />
                    {formData.id && (
                      <p className="text-xs text-gray-500 mt-1">Dejar en blanco para mantener la contraseña actual</p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="from_email">Correo Remitente *</Label>
                    <Input
                      id="from_email"
                      type="email"
                      placeholder="noreply@surnexa.cl"
                      value={formData.from_email}
                      onChange={(e) => setFormData({ ...formData, from_email: e.target.value })}
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">Dirección que aparecerá como remitente</p>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="use_tls"
                      checked={formData.use_tls}
                      onChange={(e) => setFormData({ ...formData, use_tls: e.target.checked })}
                      className="rounded"
                    />
                    <Label htmlFor="use_tls" className="cursor-pointer">Usar TLS/STARTTLS</Label>
                  </div>

                  <div className="flex gap-2 pt-4">
                    <Button type="submit" disabled={saving} className="flex-1">
                      {saving ? 'Guardando...' : 'Guardar Configuración'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleTest}
                      disabled={testing || saving}
                      className="flex-1"
                    >
                      <Mail className="mr-2 h-4 w-4" />
                      {testing ? 'Enviando...' : 'Enviar Email de Prueba'}
                    </Button>
                  </div>
                </form>

                <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h3 className="font-semibold text-blue-900 mb-2">📌 Instrucciones</h3>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• Contacta a tu proveedor de correo para obtener los datos SMTP</li>
                    <li>• La configuración SMTP es necesaria para la autenticación de dos factores (2FA)</li>
                    <li>• Usa el botón "Enviar Email de Prueba" para verificar la configuración</li>
                    <li>• Guarda los cambios antes de realizar la prueba</li>
                  </ul>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ConfiguracionSMTP;
